/**
 * Mega-menu takeover panel: toggles nav links ↔ localization list,
 * and closes the entire header menu on second localization click or country select.
 */
class MegaMenuTakeover extends HTMLElement {
  /** @type {HTMLElement | null} */
  #navPanel = null;
  /** @type {HTMLElement | null} */
  #localizationPanel = null;
  /** @type {HTMLButtonElement | null} */
  #localizationTrigger = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #countryInput = null;

  connectedCallback() {
    this.#navPanel = this.querySelector('[data-takeover-nav]');
    this.#localizationPanel = this.querySelector('[data-takeover-localization]');
    this.#localizationTrigger = this.querySelector('[data-takeover-localization-trigger]');
    this.#form = this.querySelector('.mega-menu-takeover__localization-form');
    this.#countryInput = this.querySelector('[data-takeover-country-input]');

    this.#localizationTrigger?.addEventListener('click', this.#onLocalizationTriggerClick);
    this.#localizationPanel?.addEventListener('click', this.#onCountrySelect);

    // Prevent focus/blur from closing the takeover while interacting with localization
    this.#localizationTrigger?.addEventListener('pointerdown', this.#keepMenuFocused);
    this.#localizationPanel?.addEventListener('pointerdown', this.#keepMenuFocused);

    const menuitem = this.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
    if (menuitem) {
      this.#menuitemObserver = new MutationObserver(() => {
        if (menuitem.getAttribute('aria-expanded') !== 'true') {
          this.#showNav();
        }
      });
      this.#menuitemObserver.observe(menuitem, { attributes: true, attributeFilter: ['aria-expanded'] });
    }
  }

  /** @type {MutationObserver | null} */
  #menuitemObserver = null;

  disconnectedCallback() {
    this.#localizationTrigger?.removeEventListener('click', this.#onLocalizationTriggerClick);
    this.#localizationPanel?.removeEventListener('click', this.#onCountrySelect);
    this.#localizationTrigger?.removeEventListener('pointerdown', this.#keepMenuFocused);
    this.#localizationPanel?.removeEventListener('pointerdown', this.#keepMenuFocused);
    this.#menuitemObserver?.disconnect();
  }

  /**
   * @param {PointerEvent} event
   */
  #keepMenuFocused = (event) => {
    event.preventDefault();
    this.#keepMenuOpen();
  };

  get isShowingLocalization() {
    return this.hasAttribute('data-showing-localization');
  }

  #onLocalizationTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.isShowingLocalization) {
      this.#closeMenu();
      return;
    }

    this.#showLocalization();
  };

  /**
   * @param {MouseEvent} event
   */
  #onCountrySelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('[data-country-code]');
    if (!(option instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const countryCode = option.dataset.countryCode;
    if (!countryCode || !this.#form || !this.#countryInput) return;

    if (countryCode === this.#countryInput.value) {
      this.#closeMenu();
      return;
    }

    this.#countryInput.value = countryCode;
    this.#form.submit();
  };

  #showLocalization() {
    this.setAttribute('data-showing-localization', '');
    this.#navPanel?.setAttribute('hidden', '');
    this.#localizationPanel?.removeAttribute('hidden');
    this.#localizationTrigger?.setAttribute('aria-expanded', 'true');
    this.#keepMenuOpen();
  }

  #showNav() {
    this.removeAttribute('data-showing-localization');
    this.#localizationPanel?.setAttribute('hidden', '');
    this.#navPanel?.removeAttribute('hidden');
    this.#localizationTrigger?.setAttribute('aria-expanded', 'false');
  }

  #keepMenuOpen() {
    const headerMenu = this.closest('header-menu');
    if (headerMenu && 'keepOpen' in headerMenu && typeof headerMenu.keepOpen === 'function') {
      headerMenu.keepOpen();
    }
  }

  #closeMenu() {
    this.#showNav();

    const headerMenu = this.closest('header-menu');
    if (headerMenu && 'close' in headerMenu && typeof headerMenu.close === 'function') {
      headerMenu.close();
    }
  }
}

if (!customElements.get('mega-menu-takeover')) {
  customElements.define('mega-menu-takeover', MegaMenuTakeover);
}
